import { useEffect } from 'react';
import { chatRoomName, type ChatEvent } from '@wundo/shared/chat.ts';
import { isChatEvent } from './chatEvent.ts';
import { connectRoom } from '@wundo/client/roomSocket.ts';

/**
 * Listen to the user's chat for as long as the component is mounted.
 *
 * One room and no sending: a message is posted to the API, which can answer
 * whether it was accepted, and everything the conversation produces comes back
 * here.
 *
 * `onEvent` is expected to be stable, so the socket is not torn down and
 * rebuilt on every keystroke. `onReconnect` is held to the same rule, and says
 * that the conversation should be read again: this socket carries no backlog,
 * so a reply written while it was down exists only in the database.
 */
export function useChatSocket(
  userId: string | undefined,
  onEvent: (event: ChatEvent) => void,
  onReconnect: () => void,
): void {
  useEffect(() => {
    if (!userId) {
      return;
    }

    return connectRoom(
      chatRoomName(userId),
      (data) => {
        /* Anything that is not a shape we know is ignored rather than guessed at. */
        if (isChatEvent(data)) {
          onEvent(data);
        }
      },
      onReconnect,
    );
  }, [userId, onEvent, onReconnect]);
}
